import { useMemo } from 'react';

import { createSelector } from '@reduxjs/toolkit';
import {
  TypedUseQueryHookResult,
  skipToken,
} from '@reduxjs/toolkit/dist/query/react';

import { useFetchOptionalItemsQuery } from './optionalItemsService';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import { CommonProductQueryParams } from '~/api/easy-estimator-api/commonProductQueryParams';
import { useSelectedStore } from '~/api/easy-estimator-api/stores-api/useSelectedStore';
import { shouldFetchFn } from '~/api/utils';
import { filterOutHiddenProducts } from '~/core/helpers/hidden-products.helper';
import {
  ApplianceType,
  ChipMinimizerModel,
  CutoutModel,
  OptionalProduct,
  TearOutModel,
} from '~/core/models';
import { OptionalItemsResponse } from '~/core/models/optional-items-response';
import { getProductDescription } from '~/core/utils';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectZipcode } from '~/redux/slices/zipcode';

export enum OptionalItemKeys {
  cutOuts = 'cutouts',
  tearOuts = 'tear-outs',
  chipMinimizer = 'chip-minimizer',
  others = 'others',
}

const emptyArray = [] satisfies CutoutModel[] | OptionalProduct[];

/**
 * Converts a cutout object into a draggable appliance-like type object
 * @param cutout the cutout to become draggable
 * @returns an appliance-like object with drag capabilities
 */
const makeDraggable = (cutout: CutoutModel) => ({
  ...cutout,
  applianceType: getProductDescription(cutout).match('Faucet')
    ? ApplianceType.FAUCET_CUTOUT
    : ApplianceType.ELECTRICAL_CUTOUT,
  length: 32.25,
  width: 18.5,
  depth: 9,
});

type UseOptionalItemsReturn = Omit<
  TypedUseQueryHookResult<
    OptionalItemsResponse,
    CommonProductQueryParams,
    AxiosBaseQueryFunction
  >,
  'data' | 'isFetching' | 'isError'
> & {
  cutouts: CutoutModel[];
  tearOuts: TearOutModel[];
  optionalItems: OptionalProduct[];
  /** only visible optional items, excluding hidden ones */
  visibleOptionalItems: OptionalProduct[];
  chipMinimizer?: ChipMinimizerModel;
  isFetchingOptionalItems: boolean;
  isErrorOptionalItems: boolean;
};

const selectVisibleOptionalItems = createSelector(
  [(data?: OptionalItemsResponse) => data?.[OptionalItemKeys.others] ?? []],
  (others) => filterOutHiddenProducts(others)
);

export const useOptionalItems = (): UseOptionalItemsReturn => {
  const zipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const selectedColor = useAppSelector(selectSelectedColor);

  const selectCutouts = useMemo(
    () =>
      createSelector([(cutouts?: CutoutModel[]) => cutouts], (cutouts) =>
        cutouts ? cutouts.map(makeDraggable) : emptyArray
      ),
    []
  );

  const shouldFetch = shouldFetchFn({
    args: [zipCode, selectedStore, selectedColor],
  });
  const params = {
    zipCode,
    storeId: selectedStore?.companyId,
    prodQteGrpID: selectedColor?.qteGrpID,
  };

  const {
    cutouts,
    tearOuts,
    chipMinimizer,
    optionalItems,
    visibleOptionalItems,
    isFetching: isFetchingOptionalItems,
    isError: isErrorOptionalItems,
    ...rest
  } = useFetchOptionalItemsQuery(shouldFetch ? { params } : skipToken, {
    selectFromResult: ({ data, ...rest }) => {
      const optionalItems = data?.[OptionalItemKeys.others] ?? emptyArray;

      return {
        cutouts: selectCutouts(data?.[OptionalItemKeys.cutOuts]),
        tearOuts: data?.[OptionalItemKeys.tearOuts] ?? emptyArray,
        chipMinimizer: data?.[OptionalItemKeys.chipMinimizer] ?? undefined,
        optionalItems,
        visibleOptionalItems: selectVisibleOptionalItems(data),
        ...rest,
      };
    },
  });

  // TODO: add 'others'
  return {
    cutouts,
    tearOuts,
    chipMinimizer,
    optionalItems,
    visibleOptionalItems,
    isFetchingOptionalItems,
    isErrorOptionalItems,
    ...rest,
  };
};
